import React, { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";

import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import VisionArc from "@/components/VisionArc";
import PyramidLayerGroups from "@/components/PyramidLayerGroups";
import PersonaTilesGrid from "@/components/PersonaTilesGrid";
import ServicesTilesRow from "@/components/ServicesTilesRow";
import CarrierBand from "@/components/CarrierBand";
import ToolsPreview from "@/components/ToolsPreview";
import AdvisorCard from "@/components/AdvisorCard";
import TrustBand from "@/components/TrustBand";
import FinalCTA from "@/components/FinalCTA";
import Footer from "@/components/Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [stages, setStages] = useState([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, a, s] = await Promise.all([
          apiGet("/products"),
          apiGet("/advisors"),
          apiGet("/life-stages"),
        ]);
        if (cancelled) return;
        setProducts(p || []);
        setAdvisors(a || []);
        setStages(s || []);
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load Lifespan data", e);
          setLoadError("Unable to load content. Please refresh.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useScrollReveal();

  return (
    <div data-testid="landing-page" className="min-h-screen bg-lw-ivory">
      <Nav />

      <main>
        <Hero />

        <VisionArc stages={stages} />

        <PersonaTilesGrid />

        {/* Solutions — organized by 4 wealth-pyramid layers */}
        <section
          id="solutions"
          data-testid="solutions-section"
          className="py-20 sm:py-28"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div className="reveal max-w-2xl">
                <span className="lw-eyebrow">Explore Our Solutions</span>
                <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">
                  Right product, <span className="italic">right layer.</span>
                </h2>
                <p className="mt-4 text-base leading-relaxed text-lw-muted">
                  Wealth compounds when products are layered in the right
                  order — Protect first, Accumulate next, Distribute in
                  retirement, Transfer to the next generation.
                </p>
              </div>
              <a
                href="/strategy"
                data-testid="solutions-strategy-link"
                className="btn-ghost-navy self-start lg:self-end"
              >
                See full strategy <span aria-hidden="true">→</span>
              </a>
            </div>

            {loadError && (
              <p
                data-testid="solutions-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6"
              >
                {loadError}
              </p>
            )}

            <PyramidLayerGroups products={products} />
          </div>
        </section>

        {/* Solutions by Service — direct PPC landing surface */}
        <ServicesTilesRow />

        {/* Carrier band — home banner linking to /carriers */}
        <CarrierBand />

        <ToolsPreview />

        {/* Advisors */}
        <section
          id="advisors"
          data-testid="advisors-section"
          className="py-20 sm:py-28 bg-lw-ivory-warm"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal">
              <span className="lw-eyebrow">Meet Your Advisors</span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">
                Independent advice, <span className="italic">real people.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-lw-muted">
                A small team of seasoned professionals — each specialized in a
                different chapter of your financial life.
              </p>
            </div>
            <div
              data-testid="advisors-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
            >
              {advisors.map((a) => (
                <AdvisorCard key={a.id} advisor={a} />
              ))}
            </div>
          </div>
        </section>

        <TrustBand />

        <FinalCTA />
      </main>

      <Footer />
    </div>
  );
}
